import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

export type TotalTab = {
  title: string;
  rule: {
    content: string;
    children?: {
      content: string;
    }[];
  }[];
};

const EventRules = ({ isMobile, isIB }: { isMobile: boolean; isIB: boolean }) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const titleDivScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedTab(0);
  }, [isIB]);

  const ibTab: TotalTab[] = [
    {
      title: "csr.ib_matchRegulatorTab1",
      rule: [
        ...Array.from({ length: 4 }, (_, i) => ({
          content: `csr.ib_matchRegulatorTab1Content${i + 1}`,
        })),
        {
          content: "csr.ib_matchRegulatorTab1Content5",
          children: [
            { content: "csr.ib_matchRegulatorTab1Content5Sub1" },
            { content: "csr.ib_matchRegulatorTab1Content5Sub2" },
          ],
        },
      ],
    },
    {
      title: "csr.ib_matchRegulatorTab2",
      rule: [
        {
          content: "csr.ib_matchRegulatorTab2Content1",
        },
        {
          content: "csr.ib_matchRegulatorTab2Content2",
        },
        {
          content: "csr.ib_matchRegulatorTab2Content3",
          children: [
            { content: "csr.ib_matchRegulatorTab2Content3Sub1" },
            { content: "csr.ib_matchRegulatorTab2Content3Sub2" },
            { content: "csr.ib_matchRegulatorTab2Content3Sub3" },
          ],
        },
        {
          content: "csr.ib_matchRegulatorTab2Content4",
        },
      ],
    },
    {
      title: "csr.ib_matchRegulatorTab3",
      rule: [
        ...Array.from({ length: 3 }, (_, i) => ({
          content: `csr.ib_matchRegulatorTab3Content${i + 1}`,
        })),
      ],
    },
    {
      title: "csr.ib_matchRegulatorTab4",
      rule: [
        ...Array.from({ length: 3 }, (_, i) => ({
          content: `csr.ib_matchRegulatorTab4Content${i + 1}`,
        })),
        {
          content: "csr.ib_matchRegulatorTab4Content4",
          children: [
            { content: "csr.ib_matchRegulatorTab4Content4Sub1" },
            { content: "csr.ib_matchRegulatorTab4Content4Sub2" },
            { content: "csr.ib_matchRegulatorTab4Content4Sub3" },
          ],
        },
        ...Array.from({ length: 6 }, (_, i) => ({
          content: `csr.ib_matchRegulatorTab4Content${i + 5}`,
        })),
      ],
    },
  ];

  const clientTab: TotalTab[] = [
    {
      title: "csr.client_matchRegulatorTab1",
      rule: [
        ...Array.from({ length: 4 }, (_, i) => ({
          content: `csr.client_matchRegulatorTab1Content${i + 1}`,
        })),
        {
          content: "csr.client_matchRegulatorTab1Content5",
          children: [
            { content: "csr.client_matchRegulatorTab1Content5Sub1" },
            { content: "csr.client_matchRegulatorTab1Content5Sub2" },
          ],
        },
      ],
    },
    {
      title: "csr.client_matchRegulatorTab2",
      rule: [
        ...Array.from({ length: 4 }, (_, i) => ({
          content: `csr.client_matchRegulatorTab2Content${i + 1}`,
        })),
      ],
    },
    {
      title: "csr.client_matchRegulatorTab3",
      rule: [
        ...Array.from({ length: 3 }, (_, i) => ({
          content: `csr.client_matchRegulatorTab3Content${i + 1}`,
        })),
        {
          content: "csr.client_matchRegulatorTab3Content4",
          children: [
            { content: "csr.client_matchRegulatorTab3Content4Sub1" },
            { content: "csr.client_matchRegulatorTab3Content4Sub2" },
            { content: "csr.client_matchRegulatorTab3Content4Sub3" },
          ],
        },
        ...Array.from({ length: 6 }, (_, i) => ({
          content: `csr.client_matchRegulatorTab3Content${i + 5}`,
        })),
      ],
    },
  ];

  const totalTab = isIB ? ibTab : clientTab;

  const renderTabButtons = () =>
    totalTab.map((item, index) => {
      const isSelected = selectedTab === index;
      const baseClass = "relative font-semibold";
      const mobileClass = "px-4 py-3 text-base inline-flex flex-shrink-0"; // Modified for mobile
      const desktopClass = "my-4 text-xl text-left block w-full"; // Desktop remains vertical
      const selectedStyleMobile =
        "bg-[#fff9f9] text-red after:absolute after:bottom-0 after:left-0 after:h-1 after:w-full after:bg-red";
      const selectedStyleDesktop =
        "bg-[#fff9f9] text-red after:absolute after:right-0 after:top-0 after:h-full after:w-1 after:bg-red";

      return (
        <button
          key={item.title}
          id={`rule${index + 1}`}
          aria-label={`rule${index + 1}`}
          onClick={() => {
            setSelectedTab(index);
            // Optional: scroll to center the selected tab on mobile
            if (isMobile && titleDivScrollRef.current) {
              const button = document.getElementById(`rule${index + 1}`);
              button?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
            }
          }}
          className={`${baseClass} ${isMobile ? mobileClass : desktopClass} ${
            isSelected ? (isMobile ? selectedStyleMobile : selectedStyleDesktop) : "text-black"
          }`}
        >
          <p
            className={isMobile ? "relative z-10" : "py-4 pl-8"}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(isMobile ? t(item.title).replaceAll("<br/>", "") : t(item.title)),
            }}
          />
        </button>
      );
    });

  const renderTabContent = (tab: TotalTab) => {
    return (
      <ul className="pl-0">
        {tab.rule.map((rule) => (
          <li key={rule.content} className={`text-stone-800 ${isMobile ? "mb-4 text-sm" : "mb-2 text-xl"}`}>
            <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(t(rule.content)) }}></p>
            {rule.children && (
              <ul className="pl-4">
                {rule.children.map((sub) => (
                  <li
                    key={sub.content}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(t(sub.content)) }}
                    className={`mt-2 text-[#575766] ${isMobile ? "text-sm " : "text-base"}`}
                  />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      <div className={`m-auto max-w-[1440px] text-center ${isMobile ? "mx-4 my-20 overflow-hidden" : "my-[144px]"}`}>
        <h3
          className={`text-center font-semibold text-stone-800 ${
            isMobile ? "mb-6 h-[48px] text-2xl" : "mb-10 h-[60px] text-5xl"
          }`}
        >
          {t("csr.competition_rules")}
        </h3>
        <div className="flex-col rounded-3xl border border-[#e6e8eb] shadow-sm">
          <div className={`flex ${isMobile ? "flex-col" : "flex-row"}`}>
            {isMobile ? (
              <div className="overflow-hidden rounded-t-3xl">
                <div
                  ref={titleDivScrollRef}
                  className={`scrollbar-hide flex overflow-x-auto overflow-y-hidden whitespace-nowrap border-b border-[#a1a1a1]
                    ${isMobile ? "bg-[#f8f8f8]" : ""}
                  `}
                >
                  {renderTabButtons()}
                </div>
              </div>
            ) : (
              <div className="w-[21%] border-r border-[#e6e8eb] py-8">{renderTabButtons()}</div>
            )}
            {/* Content area */}
            <div className={`text-black w-full text-left text-xl ${isMobile ? "px-4 py-6" : "px-8 py-[58px]"}`}>
              {renderTabContent(totalTab[selectedTab] || totalTab[0])}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventRules;
